import React, { useCallback, createContext, useContext, useState, useEffect } from 'react';

// Default locale
const defaultLocale = 'en';

// List of supported locales
const supportedLocales = [
  'en',
  'fr',
  'es',
  'fi',
  'id',
  'ro',
  'he',
  'hu',
  'hy',
  'th',
  'da',
  'de',
  'hr',
  'nl',
  'ar',
  'el',
  'sv',
  'pt',
  'ru',
  'uk',
  'ka',
  'it',
  'tr',
  'vi',
  'sr',
  'is',
  'sl',
  'bg',
  'sk',
  'lv',
  'no',
  'pl',
  'et',
  'cs',
  'lt',
  'pt-br',
  'ms',
  'ja',
  'ko',
  'zh-cn',
  'tj',
  'az',
  'ca',
  'kk',
  'hi',
  'bn',
  'uz',
  'tl',
  'fa',
];
const globalTranslations = {};
const loadingLocales = new Set();

export const IntlContext = createContext();

const loadTranslation = async (locale) => {
  try {
    const translation = await import(
      /* webpackChunkName: "i18n-[request]" */
      `../../../.cache/translations/${locale}.json`
    );
    return translation.default || translation;
  } catch (error) {
    console.error(`Failed to load translation for locale: ${locale}`, error);
    if (locale !== defaultLocale) {
      return loadTranslation(defaultLocale);
    }
    return {};
  }
};

/**
 * Loads all translations for the given set of locales.
 * @param {Set<string>} localesToLoad
 * @returns {Promise<void>}
 */
export const fetchTranslations = async (localesToLoad) => {
  const promises = [];
  localesToLoad.forEach((locale) => {
    if (!globalTranslations[locale] && supportedLocales.includes(locale) && !loadingLocales.has(locale)) {
      loadingLocales.add(locale);
      promises.push(
        loadTranslation(locale).then((data) => {
          globalTranslations[locale] = data;
          loadingLocales.delete(locale);
        }),
      );
    }
  });
  await Promise.all(promises);
};

// Hook to force a component to re-render when the locale changes
// This is a workaround for the fact that React doesn't re-render components when context values change
// currently only used in client browser
function useForceUpdate() {
  const [, setTick] = useState(0);
  const update = useCallback(() => setTick((tick) => tick + 1), []);
  return update;
}

/**
 * Context provider
 * @param {value} legacy initial locale
 * @returns {Promise<void>}
 */
export function IntlProvider({ children, value }) {
  const intl = useIntlProvider(value);
  return <IntlContext.Provider value={intl}>{children}</IntlContext.Provider>;
}

function useIntlProvider(initialLocale) {
  const [locale, setLocale] = useState(initialLocale || defaultLocale);
  const [resumeLocale, setResumeLocale] = useState(defaultLocale);
  const [isLoading, setIsLoading] = useState(true);
  const forceUpdate = useForceUpdate();

  const initialLocales = new Set([initialLocale || defaultLocale, defaultLocale].filter(Boolean));

  // Load all required translations up front
  useEffect(() => {
    setIsLoading(true);
    fetchTranslations(initialLocales).finally(() => setIsLoading(false));
  }, []);

  // Listen for when a locale finishes loading and force update
  useEffect(() => {
    let cancelled = false;
    const checkLoading = async () => {
      while (!cancelled) {
        if (loadingLocales.size === 0) break;
        await new Promise((res) => setTimeout(res, 100));
      }
      if (!cancelled) forceUpdate();
    };
    checkLoading();
    return () => {
      cancelled = true;
    };
  }, [locale, resumeLocale]);

  // Load translations for current locale
  useEffect(() => {
    if (!globalTranslations[locale]) {
      setIsLoading(true);
      fetchTranslations(new Set([locale])).finally(() => setIsLoading(false));
    }
  }, [locale]);

  // Load translations for resume locale
  useEffect(() => {
    if (!globalTranslations[resumeLocale]) {
      setIsLoading(true);
      fetchTranslations(new Set([resumeLocale])).finally(() => setIsLoading(false));
    }
  }, [resumeLocale]);

  // Set resume locale based on app locale
  useEffect(() => {
    resetToDefaultResumeLocale();
  }, [locale]);

  // Update locale whenever new value comes from the provider
  useEffect(() => {
    if (initialLocale && supportedLocales.includes(initialLocale)) {
      setLocale(initialLocale);
    }
  }, [initialLocale]);

  const resetToDefaultResumeLocale = () => {
    if (!locale) {
      setResumeLocale(defaultLocale);
      return;
    }

    // Map specific locales to their resume equivalent
    switch (locale) {
      case 'th':
      case 'no':
        setResumeLocale(defaultLocale);
        break;
      default:
        setResumeLocale(locale);
        break;
    }
  };

  // Change locale and reload page
  const changeLocale = (newLocale) => {
    if (supportedLocales.includes(newLocale)) {
      document.body.style.display = 'none';
      setLocale(newLocale);
      //window.location.reload();
    }
  };

  /**
   * Translation helper function to return the localized string.
   *
   * e.g. t('greeting') // "Bonjour"
   *
   * @param {string} key
   * @param {string} _locale - Locale to override the default (optional)
   */
  function t(key, _locale = locale) {
    // Handle null values
    const currentLocale = _locale || locale || defaultLocale;

    // If translation is not loaded and not loading, start loading and return ''
    if (!globalTranslations[currentLocale]) {
      if (!loadingLocales.has(currentLocale) && supportedLocales.includes(currentLocale)) {
        fetchTranslations(new Set([currentLocale])).then(forceUpdate);
      }
      return '';
    }

    // Get translation from current locale or fall back to default
    const translation = globalTranslations[currentLocale]?.[key] || globalTranslations[defaultLocale]?.[key];

    if (!translation && !isLoading) {
      console.warn(`Translation '${key}' for locale '${currentLocale}' not found.`);
    }

    return (translation || '').trim();
  }

  return {
    locale,
    setLocale: changeLocale,
    isLoading,
    resumeLocale,
    setResumeLocale,
    resetToDefaultResumeLocale,
    t,
  };
}

// Decorator HOC for legacy class components
export const withIntl = (Component) =>
  class Wrapper extends React.Component {
    static async getInitialProps(ctx) {
      const pageProps = Component.getInitialProps && (await Component.getInitialProps(ctx));
      return { ...pageProps };
    }

    render() {
      return <IntlContext.Consumer>{(state) => <Component {...this.props} {...state} />}</IntlContext.Consumer>;
    }
  };

// Component hook to access Intl methods
const useIntl = () => useContext(IntlContext);

export default useIntl;
